import React, { useState, useEffect } from "react";
import AdminService from "../services/AdminService";
import { toast } from "react-toastify";

const CountryManagement = () => {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editingCountry, setEditingCountry] = useState(null);
    const [countryForm, setCountryForm] = useState({
        name: "",
        cca2: "",
        phonecode: "",
        currency: "",
        currencySymbol: "",
        region: "",
        subregion: "",
        status: true
    });

    useEffect(() => {
        loadCountries();
    }, []);

    const loadCountries = async () => {
        setLoading(true);
        try {
            const response = await AdminService.getCountries();
            if (response.data && response.data.status) {
                const data = response.data.data.country || [];
                // Sort by ID ascending
                const sortedData = [...data].sort((a, b) => a.id - b.id);
                setCountries(sortedData);
            }
        } catch (error) {
            console.error("Error loading countries:", error);
            toast.error("Failed to load country data");
            setCountries([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const filteredCountries = countries.filter(c =>
        (c.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (c.cca2?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (c.phonecode?.toLowerCase() || "").includes(searchTerm.toLowerCase())
    );

    const openAddModal = () => {
        setEditingCountry(null);
        setCountryForm({
            name: "",
            cca2: "",
            phonecode: "",
            currency: "",
            currencySymbol: "",
            region: "",
            subregion: "",
            status: true
        });
        setShowModal(true);
    };

    const openEditModal = (country) => {
        setEditingCountry(country);
        setCountryForm({
            name: country.name || "",
            cca2: country.cca2 || "",
            phonecode: country.phonecode || "",
            currency: country.currency || "",
            currencySymbol: country.currencySymbol || "",
            region: country.region || "",
            subregion: country.subregion || "",
            status: country.status
        });
        setShowModal(true);
    };

    const handleSaveCountry = async () => {
        if (!countryForm.name || !countryForm.cca2) {
            toast.warning("Name and Code are required");
            return;
        }

        try {
            if (editingCountry) {
                await AdminService.updateCountry({ ...countryForm, id: editingCountry.id });
                toast.success("Country updated successfully");
            } else {
                await AdminService.addCountry(countryForm);
                toast.success("Country added successfully");
            }
            setShowModal(false);
            loadCountries();
        } catch (error) {
            console.error("Error saving country:", error);
            toast.error(error.response?.data?.message || "Failed to save country");
        }
    };

    const handleDeleteCountry = async (id) => {
        if (window.confirm("Are you sure you want to delete this country?")) {
            try {
                await AdminService.deleteCountry(id);
                toast.success("Country deleted successfully");
                loadCountries();
            } catch (error) {
                console.error("Error deleting country:", error);
                toast.error("Failed to delete country");
            }
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    return (
        <div className="admin-fade-in country-mgmt-container">
            {/* Improved Header Layout */}
            <div className="country-page-header">
                <div className="header-left">
                    <h2 className="header-title">Manage Countries</h2>
                    <span className="header-count">{countries.length} Total Countries</span>
                </div>
                <div className="header-right">
                    <button className="admin-btn admin-btn-primary" onClick={openAddModal}>
                        <i className="fa fa-plus-circle"></i> Add New Country
                    </button>
                </div>
            </div>

            <div className="admin-card">
                <div className="admin-card-header refined-search-header">
                    <div className="search-box-container">
                        <i className="fa fa-search search-icon"></i>
                        <input
                            type="text"
                            className="search-input-refined"
                            placeholder="Search by Country Name, ISO Code or Phone code..."
                            value={searchTerm}
                            onChange={handleSearch}
                            autoComplete="new-password"
                            name={`search_${Math.random().toString(36).substring(7)}`}
                        />
                    </div>
                    <div className="header-actions-pill">
                        <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={loadCountries}>
                            <i className="fa fa-refresh"></i>
                        </button>
                    </div>
                </div>

                <div className="admin-card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="admin-empty-state" style={{ padding: '60px 0' }}>
                            <i className="fa fa-spinner fa-spin" style={{ fontSize: '32px', color: '#2a89ba' }}></i>
                            <p style={{ marginTop: '15px', color: '#666' }}>Fetching country directory...</p>
                        </div>
                    ) : filteredCountries.length === 0 ? (
                        <div className="admin-empty-state" style={{ padding: '60px 0' }}>
                            <i className="fa fa-globe" style={{ fontSize: '48px', color: '#ccc' }}></i>
                            <p style={{ marginTop: '15px', color: '#888' }}>No results found for your search.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Country Details</th>
                                        <th>ISO Code</th>
                                        <th>Phone Code</th>
                                        <th>Currency</th>
                                        <th>Region</th>
                                        <th>Status</th>
                                        <th className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCountries.map((country) => (
                                        <tr key={country.id}>
                                            <td style={{ width: '60px' }}>
                                                <span style={{ color: '#adb5bd', fontSize: '13px' }}>#{country.id}</span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: 600, color: '#333' }}>{country.name}</span>
                                                    <span style={{ fontSize: '11px', color: '#999' }}>Added: {formatDate(country.createdAt)}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="code-badge">{country.cca2}</span>
                                            </td>
                                            <td>
                                                <span className="phone-badge">{country.phonecode}</span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <span style={{ fontWeight: 700, color: '#2a89ba' }}>{country.currencySymbol}</span>
                                                    <span style={{ fontSize: '12px' }}>{country.currency}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ fontSize: '12px' }}>
                                                    <div style={{ fontWeight: 600 }}>{country.region}</div>
                                                    <div style={{ color: '#888' }}>{country.subregion}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`admin-badge ${country.status ? 'active' : 'expired'}`}>
                                                    {country.status ? 'Live' : 'Hidden'}
                                                </span>
                                            </td>
                                            <td className="text-right">
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                    <button className="admin-action-btn edit" onClick={() => openEditModal(country)} title="Edit">
                                                        <i className="fa fa-pencil"></i>
                                                    </button>
                                                    <button className="admin-action-btn delete" onClick={() => handleDeleteCountry(country.id)} title="Delete">
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal remains largely same but ensures responsive fitting */}
            {showModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal modal-large">
                        <div className="admin-modal-header">
                            <h3>{editingCountry ? "Update Country Info" : "Register New Country"}</h3>
                            <button className="admin-modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="form-grid">
                                <div className="form-row">
                                    <div className="form-group span-8">
                                        <label>Full Country Name</label>
                                        <input
                                            type="text"
                                            className="admin-form-control"
                                            placeholder="e.g. United States"
                                            value={countryForm.name}
                                            onChange={(e) => setCountryForm({ ...countryForm, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group span-4">
                                        <label>CCA2 ISO Code</label>
                                        <input
                                            type="text"
                                            className="admin-form-control uppercase"
                                            placeholder="US"
                                            maxLength="2"
                                            value={countryForm.cca2}
                                            onChange={(e) => setCountryForm({ ...countryForm, cca2: e.target.value.toUpperCase() })}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group span-6">
                                        <label>Phone Prefix</label>
                                        <input
                                            type="text"
                                            className="admin-form-control"
                                            placeholder="+1"
                                            value={countryForm.phonecode}
                                            onChange={(e) => setCountryForm({ ...countryForm, phonecode: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group span-6">
                                        <label>Currency Name</label>
                                        <input
                                            type="text"
                                            className="admin-form-control"
                                            placeholder="US Dollar"
                                            value={countryForm.currency}
                                            onChange={(e) => setCountryForm({ ...countryForm, currency: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group span-4">
                                        <label>Symbol</label>
                                        <input
                                            type="text"
                                            className="admin-form-control"
                                            placeholder="$"
                                            value={countryForm.currencySymbol}
                                            onChange={(e) => setCountryForm({ ...countryForm, currencySymbol: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group span-4">
                                        <label>Region</label>
                                        <input
                                            type="text"
                                            className="admin-form-control"
                                            placeholder="Americas"
                                            value={countryForm.region}
                                            onChange={(e) => setCountryForm({ ...countryForm, region: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group span-4">
                                        <label>Sub-region</label>
                                        <input
                                            type="text"
                                            className="admin-form-control"
                                            placeholder="Caribbean"
                                            value={countryForm.subregion}
                                            onChange={(e) => setCountryForm({ ...countryForm, subregion: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-group" style={{ marginTop: '10px' }}>
                                    <label className="admin-status-toggle">
                                        <input
                                            type="checkbox"
                                            checked={countryForm.status}
                                            onChange={(e) => setCountryForm({ ...countryForm, status: e.target.checked })}
                                        />
                                        <span className="slider round"></span>
                                        <span className="label">Active on Public Site</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-outline" onClick={() => setShowModal(false)}>Discard</button>
                            <button className="admin-btn admin-btn-primary" onClick={handleSaveCountry}>
                                {editingCountry ? "Save Changes" : "Register Country"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Injected Scoped Styles for Refining the Page */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .country-mgmt-container {
                    padding: 10px 0;
                }
                .country-page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 25px;
                    background: #fff;
                    padding: 20px 25px;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }
                .header-left { display: flex; flex-direction: column; gap: 4px; }
                .header-title { margin: 0; font-size: 22px; color: #2a3447; font-weight: 700; }
                .header-count { font-size: 13px; color: #6c757d; font-weight: 500; }
                
                .refined-search-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 20px;
                    border-bottom: 1px solid #f0f0f0;
                }
                .search-box-container {
                    position: relative;
                    flex: 1;
                    max-width: 500px;
                }
                .search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #adb5bd;
                    font-size: 14px;
                }
                .search-input-refined {
                    width: 100%;
                    padding: 10px 10px 10px 38px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    font-size: 14px;
                    transition: all 0.2s;
                    box-sizing: border-box;
                }
                .search-input-refined:focus {
                    outline: none;
                    border-color: #2a89ba;
                    box-shadow: 0 0 0 3px rgba(42, 137, 186, 0.1);
                }

                .table-responsive { width: 100%; overflow-x: auto; }
                .code-badge { background: #f8f9fa; border: 1px solid #e9ecef; color: #495057; padding: 3px 8px; border-radius: 5px; font-weight: 700; font-size: 11px; font-family: 'JetBrains Mono', monospace; }
                .phone-badge { color: #2a89ba; font-weight: 600; font-size: 13px; }
                
                .modal-large { width: 90%; max-width: 700px; border-radius: 15px; }
                .form-grid { display: flex; flex-direction: column; gap: 15px; }
                .form-row { display: flex; gap: 20px; }
                .span-4 { flex: 0 0 calc(33.33% - 13.33px); }
                .span-6 { flex: 0 0 calc(50% - 10px); }
                .span-8 { flex: 0 0 calc(66.66% - 6.66px); }
                .admin-form-control.uppercase { text-transform: uppercase; }

                .admin-status-toggle { display: flex; align-items: center; gap: 12px; cursor: pointer; }
                .admin-status-toggle input { display: none; }
                .slider { position: relative; width: 44px; height: 22px; background-color: #ccc; transition: .4s; }
                .slider:before { position: absolute; content: ""; height: 16px; width: 16px; left: 3px; bottom: 3px; background-color: white; transition: .4s; }
                input:checked + .slider { background-color: #2a89ba; }
                input:checked + .slider:before { transform: translateX(22px); }
                .slider.round { border-radius: 34px; }
                .slider.round:before { border-radius: 50%; }
                .label { font-size: 14px; color: #495057; font-weight: 500; }
            `}} />
        </div>
    );
};

export default CountryManagement;
